import {
  priorities,
  SEVERITY_CRIT,
  SEVERITY_WARN,
  SEVERITY_OPTIONS,
  DATE_RANGE_FIELD_NAME,
  DATE_RANGE_MAX_DAYS,
} from "fleet/constant/custom-events";
import { size } from "lodash";
import find from "lodash/find";
import isEqual from "lodash/isEqual";
import moment from "moment";
import PropTypes from "prop-types";
import React from "react";
import { Badge, Row, Col } from "react-bootstrap";
import * as App from "redux-store/applications/selectors";
import * as Device from "redux-store/devices/selectors";
import { Collapsible } from "ui/collapsible";
import {
  DATE_PRESET_LAST_1_HOUR,
  DATE_PRESET_LAST_24_HOURS,
  DATE_PRESET_LAST_7_DAYS,
  Filter,
  FILTER_TYPE_DATE_RANGE,
  FILTER_TYPE_MULTI_SELECT,
  SENDER_INIT,
} from "ui/filter";
import { Notification } from "ui/notification";
import { Table, Column } from "ui/table";
import { getTargetOptions } from "util/get-target-options";
import { getErrorMessage } from "util/response";
import { sortByLabelAsc } from "util/sort-by-prop";
import { utcToLocalFormat } from "util/time";

class EventsTable extends React.PureComponent {
  static get propTypes() {
    return {
      applications: PropTypes.object.isRequired,
      device: PropTypes.object,
      devices: PropTypes.object.isRequired,
      events: PropTypes.array.isRequired,
      fetchMethod: PropTypes.func.isRequired,
    };
  }

  constructor(props) {
    super(props);

    this.filterApi = null;
    this.isInitialized = false;
    this.initialDateRange = {
      startDate: moment()
        .utc()
        .subtract(1, "hour")
        .format("YYYY-MM-DDTHH:mm:ss"),
      endDate: moment().utc().format("YYYY-MM-DDTHH:mm:ss"),
    };

    this._fetchEvents = this._fetchEvents.bind(this);
    this._formatDevice = this._formatDevice.bind(this);
    this._formatMessage = this._formatMessage.bind(this);
    this._formatSource = this._formatSource.bind(this);
    this._getTargetOptions = this._getTargetOptions.bind(this);
    this._onFilterChange = this._onFilterChange.bind(this);
    this._transformDid = this._transformDid.bind(this);
    this.getFilterApi = this.getFilterApi.bind(this);

    // This is not state, because we do not re render on these.
    this.optionsMap = {
      eventNameOptions: {},
      sourceOptions: {},
    };

    // These define the transforms from filterApi to Api params.
    this.filterToApiMapping = {
      severity: {
        transform: this._transformDefault.bind(null, Number),
        name: "priority",
      },
      source: {
        transform: this._transformDefault.bind(null, Number),
      },
      targets: {
        transform: this._transformDefault.bind(null, String),
      },
    };

    this.state = {
      isLoading: true,
      errorMessage: "",
      ...this._getAllFilterOptions([]),
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const devicesChanged = !isEqual(prevProps.devices, this.props.devices);
    const appsChanged = !isEqual(
      prevProps.applications,
      this.props.applications,
    );
    const eventsChanged = !isEqual(prevProps.events, this.props.events);

    if (appsChanged || devicesChanged || eventsChanged) {
      this.setState({
        ...this._getAllFilterOptions(this.props.events),
      });
    }
  }

  _fetchEvents(filters = {}, dates = {}) {
    const { device } = this.props;

    const { device_id: deviceId } = device || {};
    const { startDate, endDate } = dates;
    const start = startDate && startDate.utc().format("YYYY-MM-DDTHH:mm:ss");
    const end = endDate && endDate.utc().format("YYYY-MM-DDTHH:mm:ss");

    const serviceArgs = {
      deviceId,
      end,
      filters,
      start,
    };

    this.setState(
      { isLoading: true },
      this._goFetchEvents.bind(this, serviceArgs),
    );
  }

  _goFetchEvents(serviceArgs) {
    const { fetchMethod } = this.props;

    return fetchMethod(serviceArgs)
      .then((response) => {
        const newState = {
          errorMessage: "",
          ...this._getAllFilterOptions(response.data.data),
        };

        this.setState(newState);
      })
      .catch((e) => {
        this.setState({
          errorMessage: `Failed to retrieve events: ${getErrorMessage(e)}`,
        });
      })
      .finally(() => {
        this.setState({
          isLoading: false,
        });
      });
  }

  _formatDevice(did) {
    const { devices } = this.props;

    const device = devices[did];

    return device ? Device.getName(device) : `Unknown device (${did})`;
  }

  _formatSource(sourceId) {
    const { applications } = this.props;

    if (!sourceId || sourceId < 0) {
      return "Player";
    }

    const sSourceId = String(sourceId);
    const app = applications[sSourceId];

    return app ? App.name(app) : `Unknown app (${sSourceId})`;
  }

  _formatPriority(priority) {
    const priorityLevel = priorities[priority];
    if (priorityLevel) {
      return <Badge variant={priorityLevel.style}>{priorityLevel.label}</Badge>;
    }

    return null;
  }

  _formatDate(date) {
    const format = "MMM Do, YYYY hh:mm:ss a";

    return utcToLocalFormat(date, format);
  }

  _formatMessage(message, _row) {
    if (!message) {
      return <span>No message</span>;
    }
    const screenWidth = window.innerWidth;
    let maxMessageLength = 100;

    if (screenWidth <= 1280) {
      maxMessageLength = 25;
    } else if (screenWidth <= 1600) {
      maxMessageLength = 50;
    }
    if (message.length > maxMessageLength) {
      return <Collapsible label={message}>{message}</Collapsible>;
    }
    return <span title={message}>{message}</span>;
  }

  _getFilterFields() {
    const { eventNameOptions, sourceOptions, targetOptions } = this.state;

    const devicesFilter = this.props.device
      ? []
      : [
          {
            label: "Targets",
            name: "targets",
            placeholder: "Targets",
            type: FILTER_TYPE_MULTI_SELECT,
            options: targetOptions,
          },
        ];

    return [
      {
        label: "Event Name",
        name: "name",
        placeholder: "Event Name",
        type: FILTER_TYPE_MULTI_SELECT,
        options: eventNameOptions,
      },
      {
        label: "Priority",
        name: "severity",
        placeholder: "Priority",
        type: FILTER_TYPE_MULTI_SELECT,
        initialValue: [SEVERITY_WARN, SEVERITY_CRIT],
        options: SEVERITY_OPTIONS,
      },
      {
        label: "Source",
        name: "source",
        placeholder: "Source",
        type: FILTER_TYPE_MULTI_SELECT,
        options: sourceOptions,
      },
      ...devicesFilter,
      {
        label: "Event Date",
        name: DATE_RANGE_FIELD_NAME,
        type: FILTER_TYPE_DATE_RANGE,
        placeholder: {
          startDate: "Start Date",
          endDate: "End Date",
        },
        initialValue: this.initialDateRange,
        options: [
          DATE_PRESET_LAST_1_HOUR,
          DATE_PRESET_LAST_24_HOURS,
          DATE_PRESET_LAST_7_DAYS,
        ],
      },
    ];
  }

  _getFilterOptions(optionsMap) {
    return Object.entries(optionsMap)
      .map(([value, label]) => ({ label, value }))
      .sort(sortByLabelAsc);
  }

  _getAllFilterOptions(events) {
    const { eventNameOptions, sourceOptions } = this.optionsMap;

    events.forEach((event) => {
      eventNameOptions[event.name] = event.name;
      sourceOptions[event.source] = this._formatSource(event.source);
    });

    return {
      eventNameOptions: this._getFilterOptions(eventNameOptions),
      sourceOptions: this._getFilterOptions(sourceOptions),
      targetOptions: this._getTargetOptions(),
    };
  }

  _getTargetOptions() {
    const { devices } = this.props;

    return getTargetOptions(devices);
  }

  _onFilterChange(filterState) {
    const { activeFilters } = filterState;

    // The page should make an initial call to the api with default filter
    // values for initialization. We discard any future init signals
    // (SENDER_INIT) and only make requests triggered by user (SENDER_USER).
    // Filter should always update its state when there's change in fields def.
    // It is the parent's job, EventsTable in this case, to handle the behavior
    // of filter change.
    if (filterState.sender === SENDER_INIT && this.isInitialized) {
      return null;
    }

    if (!this._validateFilters(activeFilters)) {
      return;
    }

    this.isInitialized = true;

    const filterParams = {};
    let dates = {};

    activeFilters.forEach((f) => {
      if (f.name === DATE_RANGE_FIELD_NAME) {
        dates = f.value;
        return;
      }

      filterParams[f.name] = f.value;
    });

    const apiFilterParams = this._transformFilterParams(
      this.filterToApiMapping,
      filterParams,
    );

    this._fetchEvents(apiFilterParams, dates);
  }

  _transformDefault(transform, option) {
    return transform(option.value);
  }

  _transformDid(activeFilter) {
    const { devices } = this.props;
    const device = devices.get(String(activeFilter.value));

    return device ? device.device_id : "";
  }

  _transformFilterParams(filtersToParamsMap, filterParams) {
    // Turn filter parameters into something the api understands
    const defaultTransform = this._transformDefault.bind(null, String);
    const transformed = {};

    Object.entries(filterParams).forEach((entry) => {
      const [name, values] = entry;
      const params = filtersToParamsMap[name];

      if (!params) {
        transformed[name] = values.map(defaultTransform);
        return;
      }

      const newName = params.name ? params.name : name;

      transformed[newName] = params.transform
        ? values.map(params.transform)
        : values.map(defaultTransform);
    });

    return transformed;
  }

  _renderFilter() {
    const filterFields = this._getFilterFields();

    return (
      <Filter
        onFilterChange={this._onFilterChange}
        fields={filterFields}
        getApi={this.getFilterApi}
      />
    );
  }

  _validateFilters(activeFilters) {
    // Api defaults to past 1 hour. And fills each start / end value.
    // So it is valid to not have a filter value.
    const dateFilterSearch = { name: DATE_RANGE_FIELD_NAME };
    const dateFilter = find(activeFilters, dateFilterSearch);
    if (!dateFilter) {
      this.setState({ errorMessage: "" });
      return true;
    }

    const { startDate, endDate } = dateFilter.value;
    if (!startDate || !endDate) {
      this.setState({ errorMessage: "" });
      return true;
    }

    const days = endDate.diff(startDate, "days");

    if (days > DATE_RANGE_MAX_DAYS) {
      const dateFilterConfig = find(this._getFilterFields(), dateFilterSearch);
      const { label } = dateFilterConfig;

      this.setState({
        errorMessage:
          `Error: ${label} range must be ` +
          `less than ${DATE_RANGE_MAX_DAYS} days.`,
      });

      return false;
    }

    this.setState({ errorMessage: "" });

    return true;
  }

  getFilterApi(filterApi) {
    this.filterApi = filterApi;
  }

  render() {
    const { errorMessage, isLoading } = this.state;
    const { events, device } = this.props;
    const options = {
      noDataText: isLoading ? (
        <span className="text-blink">Fetching events...</span>
      ) : (
        "No events to display."
      ),
      sizePerPage: 20,
      sizePerPageList: [10, 20, 50].map((v) => ({
        text: String(v),
        value: v,
      })),
    };
    const eventData = isLoading ? [] : events;

    return (
      <Row>
        <Col xs={12}>
          <Notification error={errorMessage} />
          <Table
            classes="events-table"
            data={eventData}
            options={options}
            toolbar={this._renderFilter()}
            hover
            pagination
            search
          >
            <Column key="id" dataField="id" isKey hidden>
              ID
            </Column>
            {!device && (
              <Column
                key="did"
                dataField="did"
                dataFormat={this._formatDevice}
                width="180px"
              >
                Device
              </Column>
            )}
            <Column key="name" dataField="name" width="200px" dataSort>
              Event Name
            </Column>
            <Column
              key="severity"
              dataField="severity"
              dataFormat={this._formatPriority}
              width="100px"
              dataSort
            >
              Priority
            </Column>
            <Column
              key="message"
              dataField="message"
              dataFormat={this._formatMessage}
            >
              Message
            </Column>
            <Column
              key="event_date"
              dataField={DATE_RANGE_FIELD_NAME}
              dataFormat={this._formatDate}
              width="180px"
              dataSort
            >
              Event Date
            </Column>
            <Column
              key="source"
              dataField="source"
              dataFormat={this._formatSource}
              width="150px"
              dataSort
            >
              Source
            </Column>
          </Table>
        </Col>
      </Row>
    );
  }
}

export { EventsTable };
